import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Autocomplete, Button, Chip, FormControlLabel, IconButton, LinearProgress,
    Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import { confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../app/lang/langs";
import NetworkManager from "../../app/managers/networkManager";
// The transparent-background Vital mark, generated by the backend's
// `scripts/buildVResumeLogos.js` and copied here so the preview shows the SAME art the PDF
// embeds. NOT `logo_main.png` — that file is a JPEG (despite the extension) and therefore
// cannot be transparent, so it renders as a white block on the CV's grey rail.
import vitalLogo from "../../assets/images/logo-vital-transparent.png";
import styles from "./VResume.module.css";
import ChipsField from "../../components/vresume/ChipsField";
import RepeatingRows from "../../components/vresume/RepeatingRows";
import ExperienceCard from "../../components/vresume/ExperienceCard";
import VResumePreview from "../../components/vresume/VResumePreview";
import {
    VRESUME_LANGUAGE_LEVELS, closeEditor, deleteVResumeExperience, exportVResumePdf,
    getVResume, patchExperienceLocal, patchResumeLocal, reorderExperiencesLocal,
    reorderVResumeExperiences, saveVResume, saveVResumeExperience, syncVResumeMissions,
    clearSyncedMissions
} from "./vresumeSlice";

/**
 * Long enough that a typing burst is one request, short enough that "saved" appears while
 * the user is still looking at the field. Longer than V-Reference's 300ms search debounce on
 * purpose: a search that fires early costs a wasted query, whereas a save that fires early
 * costs a write, and there is no benefit to persisting a half-typed word.
 */
const AUTOSAVE_DEBOUNCE_MS = 700

/**
 * V-Resume — the CV editor.
 *
 * ── Save-as-you-go ───────────────────────────────────────────────────────────────────
 *
 * There is no Save button, and that is the requirement: a CV is filled in over several
 * sittings, so an unsaved-changes state that can be lost by closing a tab is the wrong
 * model. Every edit does two things:
 *
 *   1. `patchResumeLocal` / `patchExperienceLocal` applies it to the store immediately, so
 *      the input and the live preview stay responsive. Without this the fields would be
 *      controlled by a value that only updates after a round trip, which reads as dropped
 *      keystrokes.
 *   2. A debounced PATCH persists it. The backend's `save` writes only the keys present in
 *      the body, so sending one field never blanks the others.
 *
 * Pending edits are accumulated in a ref (`pendingResume` / `pendingExperiences`) rather
 * than in state: the timer callback needs the latest value at the moment it fires, and a
 * state variable captured in the closure would be the value from when the timer was set.
 *
 * ── Read-only mode ───────────────────────────────────────────────────────────────────
 *
 * `access.canEdit` comes from the server, which decided it when it resolved the caller's
 * reach over this collab. A `view`-tier manager gets the same layout with every control
 * disabled — the same screen, not a second one, so there is one place where the CV's shape
 * is defined. The server re-authorises every call regardless; disabling is a courtesy, not
 * the enforcement.
 */
export default function VResumeEditor() {

    const dispatch = useDispatch()

    const resume = useSelector((state) => state.vresume.resume)
    const experiences = useSelector((state) => state.vresume.experiences)
    const collab = useSelector((state) => state.vresume.collab)
    const access = useSelector((state) => state.vresume.access)
    const missingSections = useSelector((state) => state.vresume.missingSections)
    const syncedMissions = useSelector((state) => state.vresume.syncedMissions)
    const saveState = useSelector((state) => state.vresume.saveState)
    const postes = useSelector((state) => state.vresume.postes)
    const editingCollabId = useSelector((state) => state.vresume.editingCollabId)
    const isExporting = useSelector((state) => state.vresume.isExporting)
    const lang = useSelector((state) => state.settings.lang)
    const userId = useSelector((state) => state.login.userId)

    const resumeTimer = useRef(null)
    const experienceTimers = useRef({})
    const pendingResume = useRef({})
    const pendingExperiences = useRef({})
    const [newExperienceId, setNewExperienceId] = useState(null)
    const [dragState, setDragState] = useState({ fromId: null, overId: null })

    const canEdit = !!(access && access.canEdit)
    const isSelf = !!(access && access.isSelf)

    /**
     * The `id_collab` every write carries.
     *
     * Omitted from the body when the CV is the caller's own, so the backend falls back to
     * the session id — a client that never sends its own id cannot send the wrong one.
     */
    const targetBody = useMemo(
        () => (editingCollabId && editingCollabId != userId ? { id_collab: editingCollabId } : {}),
        [editingCollabId, userId])

    // ── Flush pending writes on unmount ──────────────────────────────────────
    //
    // Leaving the editor within the debounce window would otherwise drop the last edit,
    // which is the one thing a save-as-you-go screen must never do. The cleanup fires the
    // pending payloads immediately instead of clearing their timers.
    useEffect(() => {
        return () => {
            if (resumeTimer.current) {
                clearTimeout(resumeTimer.current)
                if (Object.keys(pendingResume.current).length) {
                    dispatch(saveVResume({ ...targetBody, ...pendingResume.current }))
                    pendingResume.current = {}
                }
            }
            Object.keys(experienceTimers.current).forEach(id => {
                clearTimeout(experienceTimers.current[id])
                const pending = pendingExperiences.current[id]
                if (pending && Object.keys(pending).length) {
                    dispatch(saveVResumeExperience({ id: Number(id), ...pending }))
                }
            })
            pendingExperiences.current = {}
            experienceTimers.current = {}
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetBody])

    // The sync toast, once per read. Cleared straight after so re-rendering does not
    // re-announce missions that were imported minutes ago.
    useEffect(() => {
        if (syncedMissions > 0) {
            toast.info(Localize(
                syncedMissions === 1 ? "VRESUME_SYNCED_ONE" : "VRESUME_SYNCED_MANY", lang)
                .replace("%count%", syncedMissions))
            dispatch(clearSyncedMissions())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [syncedMissions])

    /** Applies a resume field change locally and queues the debounced save. */
    const editResume = useCallback((changes) => {
        dispatch(patchResumeLocal(changes))
        pendingResume.current = { ...pendingResume.current, ...changes }
        if (resumeTimer.current) clearTimeout(resumeTimer.current)
        resumeTimer.current = setTimeout(() => {
            const payload = pendingResume.current
            pendingResume.current = {}
            if (Object.keys(payload).length) {
                dispatch(saveVResume({ ...targetBody, ...payload }))
            }
        }, AUTOSAVE_DEBOUNCE_MS)
    }, [dispatch, targetBody])

    /** Same, for one experience block. Debounced per block so editing two in quick
     *  succession does not make one overwrite the other's pending payload. */
    const editExperience = useCallback((id, changes) => {
        dispatch(patchExperienceLocal({ id, changes }))
        pendingExperiences.current[id] = { ...(pendingExperiences.current[id] || {}), ...changes }
        if (experienceTimers.current[id]) clearTimeout(experienceTimers.current[id])
        experienceTimers.current[id] = setTimeout(() => {
            const payload = pendingExperiences.current[id]
            delete pendingExperiences.current[id]
            delete experienceTimers.current[id]
            if (payload && Object.keys(payload).length) {
                dispatch(saveVResumeExperience({ id, ...payload }))
            }
        }, AUTOSAVE_DEBOUNCE_MS)
    }, [dispatch])

    const reload = useCallback(
        () => dispatch(getVResume(targetBody)), [dispatch, targetBody])

    // ── Experience add / remove / reorder ────────────────────────────────────

    const onAddExperience = async () => {
        const response = await dispatch(saveVResumeExperience({
            ...targetBody,
            // Seeded with the placeholder title so the row is identifiable the moment it
            // appears; the backend rejects a block with no poste, company or client.
            poste: Localize("VRESUME_EXP_NEW_TITLE", lang),
            included: 1,
            // 0 rather than the end of the list: synced Vital missions carry negative orders
            // (newest first), so 0 puts a hand-added block directly after them, which is
            // where pre-Vital experience belongs on a reverse-chronological CV.
            order: 0
        }))
        const data = response.payload && response.payload.responseData
        if (data && data.status === 1) {
            setNewExperienceId(data.id)
            await reload()
        } else {
            toast.error(Localize("VRESUME_ACTION_FAILED", lang))
        }
    }

    const onRemoveExperience = (experience) => {
        const isVital = experience.source === 'vital'
        confirmDialog({
            // Two different messages, because the two outcomes genuinely differ: a Vital
            // block is excluded and can be switched back on, a manual one is gone.
            message: Localize(isVital
                ? "VRESUME_EXP_EXCLUDE_CONFIRM" : "VRESUME_EXP_DELETE_CONFIRM", lang),
            header: Localize(isVital
                ? "VRESUME_EXP_EXCLUDE_TITLE" : "VRESUME_EXP_DELETE_TITLE", lang),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: Localize("YES", lang),
            rejectLabel: Localize("NO", lang),
            accept: async () => {
                const response = await dispatch(deleteVResumeExperience({ id: experience.id }))
                const data = response.payload && response.payload.responseData
                if (data && data.status === 1) {
                    toast.success(Localize(data.excluded
                        ? "VRESUME_EXP_EXCLUDED" : "VRESUME_EXP_DELETED", lang))
                    reload()
                } else {
                    toast.error(Localize("VRESUME_ACTION_FAILED", lang))
                }
            }
        })
    }

    /** Puts an excluded Vital block back on the CV. */
    const onRestoreExperience = (experience) => {
        editExperience(experience.id, { included: true })
    }

    const onDrop = (targetId) => {
        const { fromId } = dragState
        setDragState({ fromId: null, overId: null })
        if (!fromId || fromId === targetId) return

        const ordered = [...experiences]
        const fromIndex = ordered.findIndex(e => e.id === fromId)
        const toIndex = ordered.findIndex(e => e.id === targetId)
        if (fromIndex < 0 || toIndex < 0) return

        const [moved] = ordered.splice(fromIndex, 1)
        ordered.splice(toIndex, 0, moved)

        // Applied locally first so the row stays where it was dropped. The server does not
        // answer with the new list, so without this the row would snap back until a refetch.
        dispatch(reorderExperiencesLocal(ordered))
        dispatch(reorderVResumeExperiences({
            ...targetBody,
            ids: ordered.map(e => e.id)
        }))
    }

    const onSyncMissions = async () => {
        const response = await dispatch(syncVResumeMissions(targetBody))
        const data = response.payload && response.payload.responseData
        if (!data || data.status !== 1) return toast.error(Localize("VRESUME_ACTION_FAILED", lang))
        if (!data.syncedMissions) toast.info(Localize("VRESUME_SYNC_UP_TO_DATE", lang))
    }

    const onExport = async () => {
        const name = [collab && collab.prenom, collab && collab.nom]
            .filter(Boolean).join('_').replace(/[^A-Za-z0-9_-]/g, '') || 'CV'
        const response = await dispatch(exportVResumePdf({
            idCollab: targetBody.id_collab,
            filename: `CV_${name}.pdf`
        }))
        if (response.payload && response.payload.responseError) {
            toast.error(Localize("VRESUME_EXPORT_FAILED", lang))
        }
    }

    if (!resume) return null

    const photoUrl = NetworkManager.getInstance()
        .getCollabPhotoUrl(collab && collab.id, collab && collab.photo_updated_at)

    const isMissing = (key) => (missingSections || []).includes(key)
    const completion = resume.completion || 0

    /** A section shell with the "still empty" dot and an optional hint line. */
    const section = (titleKey, sectionKey, children, hintKey) => (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}><Langs str={titleKey} /></h3>
                {sectionKey && isMissing(sectionKey)
                    ? <Tooltip title={Localize("VRESUME_SECTION_INCOMPLETE", lang)} arrow>
                        <span className={styles.sectionFlag} />
                    </Tooltip>
                    : null}
            </div>
            <div className={styles.sectionBody}>
                {hintKey ? <p className={styles.sectionHint}><Langs str={hintKey} /></p> : null}
                {children}
            </div>
        </div>
    )

    /** The inline autosave indicator. The whole of the save feedback — there is no button. */
    const saveIndicator = () => {
        if (!canEdit) return null
        if (saveState === 'saving') return (
            <span className={styles.saveState}>
                <CloudSyncOutlinedIcon sx={{ fontSize: 15 }} />
                <Langs str="VRESUME_SAVING" />
            </span>
        )
        if (saveState === 'error') return (
            <span className={`${styles.saveState} ${styles.saveStateError}`}>
                <ErrorOutlineIcon sx={{ fontSize: 15 }} />
                <Langs str="VRESUME_SAVE_ERROR" />
            </span>
        )
        if (saveState === 'saved') return (
            <span className={`${styles.saveState} ${styles.saveStateSaved}`}>
                <CloudDoneOutlinedIcon sx={{ fontSize: 15 }} />
                <Langs str="VRESUME_SAVED" />
            </span>
        )
        return (
            <span className={styles.saveState}>
                <CloudDoneOutlinedIcon sx={{ fontSize: 15 }} />
                <Langs str="VRESUME_AUTOSAVE_ON" />
            </span>
        )
    }

    const shortText = (key, labelKey, options = {}) => (
        <TextField
            size="small" fullWidth disabled={!canEdit}
            label={<Langs str={labelKey} />}
            value={resume[key] || ''}
            onChange={(event) => editResume({ [key]: event.target.value })}
            placeholder={options.placeholderKey ? Localize(options.placeholderKey, lang) : undefined}
            multiline={!!options.multiline}
            minRows={options.minRows}
            sx={options.sx}
            InputProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
            InputLabelProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
        />
    )

    const flagSwitch = (key, labelKey, disabledReasonKey) => {
        // A flag whose underlying field is empty is switched off and disabled, with the
        // reason in a tooltip — a "show my phone number" toggle that does nothing because
        // there is no number on the record is worse than one that says so.
        const control = (
            <FormControlLabel
                control={<Switch size="small" disabled={!canEdit || !!disabledReasonKey}
                    checked={!!resume[key]}
                    onChange={(event) => editResume({ [key]: event.target.checked })} />}
                label={<span style={{ fontFamily: 'roboto', fontSize: 12.5 }}>
                    <Langs str={labelKey} />
                </span>}
            />
        )
        if (!disabledReasonKey) return control
        return (
            <Tooltip title={Localize(disabledReasonKey, lang)} arrow key={key}>
                <span>{control}</span>
            </Tooltip>
        )
    }

    return (
        <div className={styles.vresume}>

            {/* ── Header ─────────────────────────────────────────────────── */}
            <div className={styles.pageHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    {/* Present even when the editor is the only thing a collab can see: it
                        returns to the listing, which for them holds their own CV alone —
                        consistent navigation beats a button that is sometimes absent. */}
                    <Tooltip title={Localize("VRESUME_BACK", lang)} arrow>
                        <IconButton size="small" onClick={() => dispatch(closeEditor())}>
                            <ArrowBackIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                    <h2 className={styles.pageTitle}>
                        {isSelf
                            ? <Langs str="VRESUME_MY_CV" />
                            : [collab && collab.prenom, collab && collab.nom].filter(Boolean).join(' ')}
                    </h2>
                    {!canEdit
                        ? <Chip size="small" label={<Langs str="VRESUME_READ_ONLY" />}
                            sx={{
                                fontFamily: 'roboto', fontSize: 11,
                                backgroundColor: 'var(--lightGrey)', color: '#5a6070'
                            }} />
                        : null}
                </div>

                <div className={styles.headerActions}>
                    {saveIndicator()}
                    {canEdit
                        ? <Tooltip title={Localize("VRESUME_SYNC_TOOLTIP", lang)} arrow>
                            <Button size="small" startIcon={<SyncIcon sx={{ fontSize: 16 }} />}
                                onClick={onSyncMissions}
                                sx={{
                                    textTransform: 'none', fontFamily: 'roboto', fontSize: 12.5,
                                    color: 'var(--vital-blue)'
                                }}>
                                <Langs str="VRESUME_SYNC" />
                            </Button>
                        </Tooltip>
                        : null}
                    <Button size="small" variant="contained" disabled={isExporting}
                        startIcon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />}
                        onClick={onExport}
                        sx={{
                            textTransform: 'none', fontFamily: 'roboto', fontSize: 12.5,
                            backgroundColor: 'var(--orange)',
                            '&:hover': { backgroundColor: '#e07f0d' }
                        }}>
                        <Langs str="VRESUME_EXPORT" />
                    </Button>
                </div>
            </div>

            {/* ── Completion ─────────────────────────────────────────────── */}
            <div className={styles.section} style={{ marginBottom: 14 }}>
                <div className={styles.sectionBody} style={{ paddingTop: 12, paddingBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {completion >= 100
                            ? <CheckCircleIcon sx={{ fontSize: 20, color: 'var(--green)' }} />
                            : null}
                        <div style={{ flex: '1 1 auto' }}>
                            <div style={{
                                display: 'flex', justifyContent: 'space-between',
                                fontFamily: 'roboto', fontSize: 12, marginBottom: 5
                            }}>
                                <span style={{ fontWeight: 600, color: '#5a6070' }}>
                                    <Langs str="VRESUME_COMPLETION" />
                                </span>
                                <span style={{ fontWeight: 700, color: 'var(--vital-blue)' }}>
                                    {completion}%
                                </span>
                            </div>
                            <LinearProgress
                                variant="determinate" value={completion}
                                sx={{
                                    height: 6, borderRadius: 3,
                                    backgroundColor: 'var(--lightGrey)',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 3,
                                        backgroundColor: completion >= 100 ? 'var(--green)' : 'var(--orange)'
                                    }
                                }}
                            />
                        </div>
                    </div>
                    {/* The list of what is missing, not just the number: a progress bar on
                        its own tells a collab they are not done without telling them what
                        to do about it. */}
                    {(missingSections || []).length
                        ? <div style={{ marginTop: 10, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                            {missingSections.map(key => (
                                <Chip key={key} size="small"
                                    label={Localize(`VRESUME_MISSING_${key.toUpperCase()}`, lang)}
                                    sx={{
                                        fontFamily: 'roboto', fontSize: 11,
                                        backgroundColor: 'rgba(255, 144, 21, 0.12)',
                                        color: '#8a4b00'
                                    }} />
                            ))}
                        </div>
                        : null}
                </div>
            </div>

            {/* ── Editor + preview ───────────────────────────────────────── */}
            <div className={styles.editorLayout}>
                <div className={styles.editorPane}>

                    {section("VRESUME_SECTION_IDENTITY", null, (
                        <div>
                            <p className={styles.sectionHint}>
                                <Langs str="VRESUME_IDENTITY_HINT" />
                            </p>

                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {/*
                                  * freeSolo: the `postes` referential is a scheduling
                                  * catalogue and will not hold every title a CV needs, and
                                  * being pushed toward a wrong nearby option is worse than
                                  * typing the right one. Picking from the list also sets
                                  * `id_poste`, which is what makes a CV findable by post.
                                  */}
                                <Autocomplete
                                    freeSolo disabled={!canEdit}
                                    options={(postes || []).map(poste => poste.name).filter(Boolean)}
                                    value={resume.headline || ''}
                                    onInputChange={(event, value) => {
                                        const match = (postes || []).find(poste => poste.name === value)
                                        editResume({
                                            headline: value,
                                            id_poste: match ? match.id : null
                                        })
                                    }}
                                    sx={{ flex: '1 1 260px' }}
                                    renderInput={(params) => (
                                        <TextField {...params} size="small"
                                            label={<Langs str="VRESUME_HEADLINE" />}
                                            placeholder={Localize("VRESUME_HEADLINE_PLACEHOLDER", lang)}
                                            InputProps={{ ...params.InputProps, sx: { fontFamily: 'roboto', fontSize: 13 } }}
                                            InputLabelProps={{ sx: { fontFamily: 'roboto', fontSize: 13 } }}
                                        />
                                    )}
                                />

                                {/* The CV's own language, not the app's. Drives both the
                                    preview's and the PDF's section headings. */}
                                <ToggleButtonGroup
                                    exclusive size="small" disabled={!canEdit}
                                    value={resume.locale || 'fr'}
                                    onChange={(event, value) => value && editResume({ locale: value })}
                                    sx={{ height: 40 }}>
                                    <ToggleButton value="fr" sx={{ textTransform: 'none', fontFamily: 'roboto', fontSize: 12.5, px: 1.5 }}>
                                        <Langs str="VRESUME_LOCALE_FR" />
                                    </ToggleButton>
                                    <ToggleButton value="en" sx={{ textTransform: 'none', fontFamily: 'roboto', fontSize: 12.5, px: 1.5 }}>
                                        <Langs str="VRESUME_LOCALE_EN" />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </div>

                            <div style={{ marginTop: 12 }}>
                                {shortText("summary", "VRESUME_SUMMARY", {
                                    multiline: true, minRows: 4,
                                    placeholderKey: "VRESUME_SUMMARY_PLACEHOLDER"
                                })}
                            </div>

                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                                <div style={{ flex: '1 1 180px' }}>
                                    {shortText("availability", "VRESUME_AVAILABILITY")}
                                </div>
                                <div style={{ flex: '1 1 180px' }}>
                                    {shortText("mobility", "VRESUME_MOBILITY")}
                                </div>
                                <div style={{ flex: '1 1 140px' }}>
                                    {shortText("driving_license", "VRESUME_LICENSE")}
                                </div>
                            </div>
                        </div>
                    ))}

                    {section("VRESUME_SECTION_DISPLAY", null, (
                        <div>
                            <p className={styles.sectionHint}>
                                <Langs str="VRESUME_DISPLAY_HINT" />
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 14 }}>
                                {flagSwitch("show_photo", "VRESUME_SHOW_PHOTO",
                                    collab && collab.photo_updated_at ? null : "VRESUME_NO_PHOTO_ON_FILE")}
                                {flagSwitch("show_vital_logo", "VRESUME_SHOW_LOGO")}
                                {flagSwitch("show_email", "VRESUME_SHOW_EMAIL",
                                    collab && collab.email ? null : "VRESUME_NO_EMAIL_ON_FILE")}
                                {flagSwitch("show_phone", "VRESUME_SHOW_PHONE",
                                    collab && (collab.mobile_number || collab.telephone_number)
                                        ? null : "VRESUME_NO_PHONE_ON_FILE")}
                                {flagSwitch("show_city", "VRESUME_SHOW_CITY",
                                    collab && collab.city ? null : "VRESUME_NO_CITY_ON_FILE")}
                                {flagSwitch("show_birth_date", "VRESUME_SHOW_BIRTH_DATE",
                                    collab && collab.date_naissance ? null : "VRESUME_NO_BIRTH_DATE_ON_FILE")}
                            </div>
                        </div>
                    ))}

                    {section("VRESUME_SECTION_EXPERIENCE", "experiences", (
                        <div>
                            {(experiences || []).length === 0
                                ? <div className={styles.emptyRows}>
                                    <Langs str="VRESUME_NO_EXPERIENCE" />
                                </div>
                                : experiences.map(experience => (
                                    <ExperienceCard
                                        key={experience.id}
                                        experience={experience}
                                        lang={lang}
                                        disabled={!canEdit}
                                        defaultExpanded={experience.id === newExperienceId}
                                        isDropTarget={dragState.overId === experience.id}
                                        onChange={(changes) => editExperience(experience.id, changes)}
                                        onRemove={() => onRemoveExperience(experience)}
                                        onRestore={() => onRestoreExperience(experience)}
                                        dragHandlers={{
                                            draggable: true,
                                            onDragStart: () => setDragState({ fromId: experience.id, overId: null }),
                                            onDragOver: (event) => {
                                                // preventDefault is what makes the element a
                                                // valid drop target at all; without it the
                                                // browser refuses the drop.
                                                event.preventDefault()
                                                if (dragState.overId !== experience.id) {
                                                    setDragState(state => ({ ...state, overId: experience.id }))
                                                }
                                            },
                                            onDragEnd: () => setDragState({ fromId: null, overId: null }),
                                            onDrop: (event) => {
                                                event.preventDefault()
                                                onDrop(experience.id)
                                            }
                                        }}
                                    />
                                ))}

                            {canEdit
                                ? <Button size="small" startIcon={<AddIcon />} onClick={onAddExperience}
                                    sx={{
                                        mt: 1, textTransform: 'none', fontFamily: 'roboto',
                                        fontSize: 12.5, color: 'var(--orange)'
                                    }}>
                                    <Langs str="VRESUME_ADD_EXPERIENCE" />
                                </Button>
                                : null}
                        </div>
                    ), "VRESUME_EXPERIENCE_HINT")}

                    {section("VRESUME_SECTION_EDUCATION", "education", (
                        <RepeatingRows
                            rows={resume.education || []}
                            onChange={(rows) => editResume({ education: rows })}
                            disabled={!canEdit}
                            addLabel={Localize("VRESUME_ADD_EDUCATION", lang)}
                            emptyLabel={Localize("VRESUME_NO_EDUCATION", lang)}
                            removeTooltip={Localize("VRESUME_REMOVE_ROW", lang)}
                            fields={[
                                // Free text, not a number: a French CV writes "2018", "2018 – 2021"
                                // or "en cours", and the last is unrepresentable as an integer.
                                { key: 'year_from', label: Localize("VRESUME_YEAR_FROM", lang), width: 96 },
                                { key: 'year_to', label: Localize("VRESUME_YEAR_TO", lang), width: 96 },
                                { key: 'diploma', label: Localize("VRESUME_DIPLOMA", lang) },
                                { key: 'school', label: Localize("VRESUME_SCHOOL", lang) },
                                { key: 'city', label: Localize("VRESUME_CITY", lang), width: 120 }
                            ]}
                        />
                    ))}

                    {section("VRESUME_SECTION_SKILLS", "skills", (
                        <ChipsField
                            value={resume.skills || []}
                            onChange={(items) => editResume({ skills: items })}
                            disabled={!canEdit}
                            label={<Langs str="VRESUME_SKILLS" />}
                            placeholder={Localize("VRESUME_SKILLS_PLACEHOLDER", lang)}
                            helperText={Localize("VRESUME_CHIPS_HELPER", lang)}
                        />
                    ))}

                    {section("VRESUME_SECTION_LANGUAGES", "languages", (
                        <RepeatingRows
                            rows={resume.languages || []}
                            onChange={(rows) => editResume({ languages: rows })}
                            disabled={!canEdit}
                            addLabel={Localize("VRESUME_ADD_LANGUAGE", lang)}
                            emptyLabel={Localize("VRESUME_NO_LANGUAGES", lang)}
                            removeTooltip={Localize("VRESUME_REMOVE_ROW", lang)}
                            fields={[
                                { key: 'name', label: Localize("VRESUME_LANGUAGE", lang) },
                                {
                                    key: 'level', label: Localize("VRESUME_LEVEL", lang),
                                    type: 'select', width: 190,
                                    options: VRESUME_LANGUAGE_LEVELS.map(level => ({
                                        value: level,
                                        label: Localize(`VRESUME_LEVEL_${level.toUpperCase()}`, lang)
                                    }))
                                }
                            ]}
                        />
                    ))}

                    {section("VRESUME_SECTION_CERTIFICATIONS", null, (
                        <RepeatingRows
                            rows={resume.certifications || []}
                            onChange={(rows) => editResume({ certifications: rows })}
                            disabled={!canEdit}
                            addLabel={Localize("VRESUME_ADD_CERTIFICATION", lang)}
                            emptyLabel={Localize("VRESUME_NO_CERTIFICATIONS", lang)}
                            removeTooltip={Localize("VRESUME_REMOVE_ROW", lang)}
                            fields={[
                                { key: 'name', label: Localize("VRESUME_CERTIFICATION", lang) },
                                { key: 'issuer', label: Localize("VRESUME_ISSUER", lang) },
                                { key: 'year', label: Localize("VRESUME_YEAR", lang), width: 96 }
                            ]}
                        />
                    ))}

                    {section("VRESUME_SECTION_INTERESTS", null, (
                        <ChipsField
                            value={resume.interests || []}
                            onChange={(items) => editResume({ interests: items })}
                            disabled={!canEdit}
                            label={<Langs str="VRESUME_INTERESTS" />}
                            placeholder={Localize("VRESUME_INTERESTS_PLACEHOLDER", lang)}
                            helperText={Localize("VRESUME_CHIPS_HELPER", lang)}
                        />
                    ), "VRESUME_INTERESTS_HINT")}
                </div>

                {/* ── Live preview ───────────────────────────────────────── */}
                <div className={styles.previewPane}>
                    <div className={styles.preview}>
                        <div className={styles.previewHeader}>
                            <span className={styles.previewLabel}><Langs str="VRESUME_PREVIEW" /></span>
                            <span className={styles.headerScope}><Langs str="VRESUME_PREVIEW_NOTE" /></span>
                        </div>
                        <VResumePreview
                            resume={resume}
                            experiences={experiences}
                            collab={collab}
                            photoUrl={photoUrl}
                            logoSrc={vitalLogo}
                            // Placeholders only while editing: a read-only viewer is looking
                            // at a finished CV and should see it as the PDF renders it.
                            showPlaceholders={canEdit}
                            lang={lang}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
